import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

// API URL configuration - uses relative URL in production (nginx proxies to backend)
const API_URL = process.env.REACT_APP_API_URL || 
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

const api = axios.create({ baseURL: API_URL });

const TemplateScreen = () => {
  const { isReadOnly } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [sections, setSections] = useState([]);
  const [partTypes, setPartTypes] = useState({});
  const [loading, setLoading] = useState(false);
  const [cloneFromId, setCloneFromId] = useState('');

  const readOnlyMode = isReadOnly('/templates');

  // Fetch all templates
  useEffect(() => {
    api.get('/templates').then(res => setTemplates(res.data));
  }, []);

  // Fetch sections and part types for selected template
  useEffect(() => {
    if (!selectedTemplate) {
      setSections([]);
      setPartTypes({});
      setTemplateName('');
      setTemplateDesc('');
      return;
    }
    setLoading(true);
    api.get('/templates/' + selectedTemplate.template_id).then(res => {
      setTemplateName(res.data.template_name);
      setTemplateDesc(res.data.description || '');
    });
    api.get('/template_sections', { params: { template_id: selectedTemplate.template_id } })
      .then(res => {
        setSections(res.data);
        // For each section, fetch part types
        Promise.all(res.data.map(sec =>
          api.get('/template_part_types', { params: { section_id: sec.section_id } })
        )).then(results => {
          const pt = {};
          res.data.forEach((sec, i) => { pt[sec.section_id] = results[i].data; });
          setPartTypes(pt);
          setLoading(false);
        });
      });
  }, [selectedTemplate]);

  // Create or update template
  const handleTemplateSave = async e => {
    e.preventDefault();
    if (readOnlyMode) return;
    if (selectedTemplate) {
      const res = await api.put(`/templates/${selectedTemplate.template_id}`, {
        template_name: templateName,
        description: templateDesc
      });
      setTemplates(templates.map(t =>
        t.template_id === res.data.template_id ? res.data : t
      ));
    } else {
      let res;
      if (cloneFromId) {
        res = await api.post(`/templates/${cloneFromId}/clone`, {
          template_name: templateName,
          description: templateDesc
        });
      } else {
        res = await api.post('/templates', {
          template_name: templateName,
          description: templateDesc
        });
      }
      setTemplates([...templates, res.data]);
      setSelectedTemplate(res.data);
    }
  };

  // Section CRUD
  const handleAddSection = async () => {
    if (readOnlyMode) return;
    if (!selectedTemplate) return;
    const res = await api.post('/template_sections', {
      template_id: selectedTemplate.template_id,
      section_name: 'New Section',
      section_order: sections.length
    });
    setSections([...sections, res.data]);
    setPartTypes(prev => ({ ...prev, [res.data.section_id]: [] }));
  };

  const handleUpdateSection = async (section_id, updates) => {
    const sec = sections.find(s => s.section_id === section_id);
    const res = await api.put(`/template_sections/${section_id}`, {
      section_name: updates.section_name ?? sec.section_name,
      section_order: updates.section_order ?? sec.section_order
    });
    setSections(sections.map(s => s.section_id === section_id ? res.data : s));
  };

  const handleDeleteSection = async section_id => {
    if (readOnlyMode) return;
    await api.delete(`/template_sections/${section_id}`);
    setSections(sections.filter(s => s.section_id !== section_id));
    const pt = { ...partTypes };
    delete pt[section_id];
    setPartTypes(pt);
  };

  // Part Type CRUD
  const handleAddPartType = async section_id => {
    if (readOnlyMode) return;
    const res = await api.post('/template_part_types', {
      section_id,
      part_type_name: 'New Part Type',
      part_type_order: partTypes[section_id]?.length || 0
    });
    setPartTypes({
      ...partTypes,
      [section_id]: [...(partTypes[section_id] || []), res.data]
    });
  };

  const handleUpdatePartType = async (section_id, part_type_id, updates) => {
    if (readOnlyMode) return;
    const pt = partTypes[section_id].find(pt => pt.part_type_id === part_type_id);
    const res = await api.put(`/template_part_types/${part_type_id}`, {
      part_type_name: updates.part_type_name ?? pt.part_type_name,
      part_type_order: updates.part_type_order ?? pt.part_type_order
    });
    setPartTypes({
      ...partTypes,
      [section_id]: partTypes[section_id].map(pt =>
        pt.part_type_id === part_type_id ? res.data : pt
      )
    });
  };

  const handleDeletePartType = async (section_id, part_type_id) => {
    if (readOnlyMode) return;
    await api.delete(`/template_part_types/${part_type_id}`);
    setPartTypes({
      ...partTypes,
      [section_id]: partTypes[section_id].filter(pt => pt.part_type_id !== part_type_id)
    });
  };

  // Drag and drop handlers
  const onDragEnd = async result => {
    if (readOnlyMode) return;
    if (!result.destination) return;
    const { source, destination, type } = result;

    if (type === 'section') {
      const reordered = Array.from(sections);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);
      // Update order in DB and state
      await Promise.all(reordered.map((sec, idx) =>
        handleUpdateSection(sec.section_id, { section_order: idx })
      ));
      setSections(reordered);
    } else if (type === 'partType') {
      // Find which section this drag happened in
      const sourceSectionId = parseInt(source.droppableId.replace('partTypes-', ''), 10);
      const reordered = Array.from(partTypes[sourceSectionId]);
      const [removed] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, removed);
      // Update order in DB and state
      await Promise.all(reordered.map((pt, idx) =>
        handleUpdatePartType(sourceSectionId, pt.part_type_id, { part_type_order: idx })
      ));
      setPartTypes({ ...partTypes, [sourceSectionId]: reordered });
    }
  };

  return (
    <div>
      <h1>Templates</h1>
      {readOnlyMode && (
        <p style={{ color: '#666', fontStyle: 'italic', marginBottom: '1rem' }}>
          Read-only mode: You can view but not modify templates
        </p>
      )}
      <div style={{ display: 'flex', gap: 32 }}>
        {/* Template List */}
        <div>
          <h2>All Templates</h2>
          <ul>
            {templates.map(t => (
              <li key={t.template_id}>
                <button
                  style={{
                    fontWeight: selectedTemplate?.template_id === t.template_id ? 'bold' : 'normal'
                  }}
                  onClick={() => setSelectedTemplate(t)}
                >
                  {t.template_name}
                </button>
              </li>
            ))}
          </ul>
        </div>
        {/* Template Editor */}
        <div style={{ flex: 1 }}>
          <h2>{selectedTemplate ? (readOnlyMode ? 'View Template' : 'Edit Template') : 'Create Template'}</h2>
          {!readOnlyMode && (
            <form onSubmit={handleTemplateSave}>
              <div>
                <label>Name:</label>
              <input
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                required
                style={{ width: 300 }}
              />
            </div>
            <div>
              <label>Description:</label>
              <input
                value={templateDesc}
                onChange={e => setTemplateDesc(e.target.value)}
                style={{ width: 400 }}
              />
            </div>
            <button type="submit" style={{ marginTop: 8 }}>
              {selectedTemplate ? 'Update Template' : 'Create Template'}
            </button>
          </form>
          )}
          {!readOnlyMode && !selectedTemplate && (
            <div>
              <label>Clone from existing template:</label>
              <select
                value={cloneFromId}
                onChange={e => setCloneFromId(e.target.value)}
                style={{ width: 300, marginLeft: 8 }}
              >
                <option value="">-- None --</option>
                {templates.map(t => (
                  <option key={t.template_id} value={t.template_id}>
                    {t.template_name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {readOnlyMode && selectedTemplate && (
            <div style={{ marginTop: 16 }}>
              <div>
                <strong>Name:</strong> {templateName}
              </div>
              <div style={{ marginTop: 8 }}>
                <strong>Description:</strong> {templateDesc}
              </div>
            </div>
          )}
          {selectedTemplate && (
            <div style={{ marginTop: 32 }}>
              <h3>Sections & Part Types</h3>
              {!readOnlyMode && (
                <button onClick={handleAddSection}>+ Add Section</button>
              )}
              {loading ? (
                <div>Loading...</div>
              ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="sections" type="section">
                    {provided => (
                      <ul
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{ listStyle: 'none', padding: 0 }}
                      >
                        {sections.map((section, secIdx) => (
                          <Draggable
                            key={section.section_id}
                            draggableId={`section-${section.section_id}`}
                            index={secIdx}
                          >
                            {providedSec => (
                              <li
                                ref={providedSec.innerRef}
                                {...providedSec.draggableProps}
                                style={{
                                  ...providedSec.draggableProps.style,
                                  border: '1px solid #ccc',
                                  margin: '8px 0',
                                  padding: 8,
                                  background: '#f9f9f9'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                  <span {...providedSec.dragHandleProps} style={{ cursor: 'grab', marginRight: 8 }}>☰</span>
                                  <input
                                    value={section.section_name}
                                    onChange={e =>
                                      handleUpdateSection(section.section_id, { section_name: e.target.value })
                                    }
                                    style={{ fontWeight: 'bold', width: 200 }}
                                  />
                                  <button
                                    onClick={() => handleDeleteSection(section.section_id)}
                                    style={{ 
                                      marginLeft: 8, 
                                      backgroundColor: '#dc3545', 
                                      color: 'white', 
                                      border: 'none', 
                                      borderRadius: '4px', 
                                      padding: '0.25rem 0.5rem', 
                                      cursor: 'pointer',
                                      transition: 'background-color 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                                  >
                                    Delete Section
                                  </button>
                                  <button
                                    onClick={() => handleAddPartType(section.section_id)}
                                    style={{ marginLeft: 8 }}
                                  >
                                    + Add Part Type
                                  </button>
                                </div>
                                {/* Part Types */}
                                <Droppable
                                  droppableId={`partTypes-${section.section_id}`}
                                  type="partType"
                                >
                                  {providedPT => (
                                    <ul
                                      ref={providedPT.innerRef}
                                      {...providedPT.droppableProps}
                                      style={{ listStyle: 'none', paddingLeft: 32 }}
                                    >
                                      {(partTypes[section.section_id] || []).map((pt, ptIdx) => (
                                        <Draggable
                                          key={pt.part_type_id}
                                          draggableId={`partType-${pt.part_type_id}`}
                                          index={ptIdx}
                                        >
                                          {providedPTItem => (
                                            <li
                                              ref={providedPTItem.innerRef}
                                              {...providedPTItem.draggableProps}
                                              style={{
                                                ...providedPTItem.draggableProps.style,
                                                border: '1px solid #eee',
                                                margin: 0,           // No margin
                                                padding: 0,          // No padding
                                                background: '#fff'
                                              }}
                                            >
                                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span
                                                  {...providedPTItem.dragHandleProps}
                                                  style={{ cursor: 'grab', marginRight: 8, userSelect: 'none' }}
                                                  tabIndex={-1}
                                                >⋮</span>
                                                <input
                                                  value={pt.part_type_name}
                                                  onChange={e =>
                                                    handleUpdatePartType(
                                                      section.section_id,
                                                      pt.part_type_id,
                                                      { part_type_name: e.target.value }
                                                    )
                                                  }
                                                  style={{ width: 180 }}
                                                />
                                                <button
                                                  onClick={() =>
                                                    handleDeletePartType(section.section_id, pt.part_type_id)
                                                  }
                                                  style={{ 
                                                    marginLeft: 8, 
                                                    backgroundColor: '#dc3545', 
                                                    color: 'white', 
                                                    border: 'none', 
                                                    borderRadius: '4px', 
                                                    padding: '0.25rem 0.5rem', 
                                                    cursor: 'pointer',
                                                    transition: 'background-color 0.2s ease'
                                                  }}
                                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                                                  onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                                                >
                                                  Delete
                                                </button>
                                              </div>
                                            </li>
                                          )}
                                        </Draggable>
                                      ))}
                                      {providedPT.placeholder}
                                    </ul>
                                  )}
                                </Droppable>
                              </li>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </ul>
                    )}
                  </Droppable>
                </DragDropContext>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TemplateScreen;